import { handleApiError, handleApiResponse } from "@/methods/api";
import ApiVerified from "@/services/apiVerified";
import {
  ActivityClassification,
  PredesignedActivityWire,
  PublishPredesignedActivityInputs,
  StudentPredesignedActivityWire,
  UpdateActivitySubtypeInputs,
  UpdatePredesignedActivitiesStatusInputs,
  UpdatePredesignedActivitiesTypeInputs,
} from "@/types/activity";
import { ApiResponse, IdNamePair } from "@/types/global";

export default class ActivityPredesignedApi extends ApiVerified {
  async getPredesignedActivitiesForStudent(
    classification: ActivityClassification,
    userId: number,
    courseId: number,
    classId: number,
    subjectId: number | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<StudentPredesignedActivityWire>>(
        `activity-predesigned/predesigned-activities-for-student`,
        {
          params: {
            activityTypeId: classification.ActivityTypeId,
            userId: userId,
            courseId: courseId,
            classId: classId,
            subjectId: subjectId,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getPredesignedActivitiesForAdmin(
    courseId: number,
    classification: ActivityClassification,
    onlyActive: boolean = true,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<PredesignedActivityWire>>(
        `activity-predesigned/predesigned-activities-for-admin`,
        {
          params: {
            courseId: courseId,
            activityTypeId: classification.ActivityTypeId,
            activityCategoryId: classification.ActivityCategoryId,
            // TODO(backend): activityScopeId not yet accepted by the endpoint — sent
            // so the "Activity For" filter works once added. See
            // plans/predesigned-admin-backend-todos.md.
            activityScopeId: classification.ActivityScopeId,
            onlyActive: onlyActive,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getPredesignedActivitiesForAffiliation(
    classification: ActivityClassification,
    courseId: number,
    affiliationId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<PredesignedActivityWire>>(
        `activity-predesigned/predesigned-activities-for-affiliation`,
        {
          params: {
            activityTypeId: classification.ActivityTypeId,
            activityScopeId: classification.ActivityScopeId,
            activityCategoryId: classification.ActivityCategoryId,
            courseId: courseId,
            affiliationId: affiliationId,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getPredesignedActivitiesForChapter(
    subjectChapterId: number,
    classification: ActivityClassification,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `activity-predesigned/predesigned-activities-for-chapter`,
        {
          params: {
            subjectChapterId: subjectChapterId,
            activityTypeId: classification.ActivityTypeId,
            activityScopeId: classification.ActivityScopeId,
            activityCategoryId: classification.ActivityCategoryId,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getPredesignedActivityById(
    activityId: number,
    affiliationId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PredesignedActivityWire>(
        `activity-predesigned/predesigned-activity-by-id`,
        { params: { activityId: activityId, affiliationId: affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updatePredesignedActivitiesType(
    updateInputs: UpdatePredesignedActivitiesTypeInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `activity-predesigned/update-predesigned-activities-type`,
        updateInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updatePredesignedActivitiesStatus(
    updateInputs: UpdatePredesignedActivitiesStatusInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `activity-predesigned/update-predesigned-activities-status`,
        updateInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async resetPredesignedActivityForStudent(
    userId: number,
    activityId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<boolean>(
        `activity-predesigned/reset-predesigned-activity-for-student`,
        { params: { userId: userId, activityId: activityId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // AR-19.2 (AR-0.4): `student-predesigned-activity-by-qr-content` was removed (404). The QR
  // launch now runs a two-step recipe: (1) get-qr-code-details resolves ActivityId off the
  // QrCodeContentDisplay (already fetched upstream by qrCodeScannerStore), then
  // (2) validatePredesignedActivityFromQrCode confirms the activity exists and is predesigned.
  // See studentPredesignedActivitiesStore.

  async validatePredesignedActivityFromQrCode(activityId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<boolean>(
        `activity-predesigned/${activityId}/validate-qr`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async publishPredesignedActivity(
    publishPredesignedActivityInputs: PublishPredesignedActivityInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `activity-predesigned/publish-predesigned-activity`,
        publishPredesignedActivityInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateActivitySubtype(updateInputs: UpdateActivitySubtypeInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `activity-predesigned/${updateInputs.ActivityId}/sub-type`,
        updateInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
